import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { get } from "lodash";
import moment from "moment-timezone";
// Month and weekday names in the matrix's languages. Each import also makes
// its locale moment's global one, so English is restored right after.
import "moment/locale/fr";
import "moment/locale/ja";
import "moment/locale/zh-tw";
import "moment/locale/zh-cn";
import { confirm } from "react-confirm-box";
import Timeline, {
  CustomMarker,
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
  CustomHeader,
} from "react-calendar-timeline";
import "react-calendar-timeline/lib/Timeline.css";
import "react-sliding-side-panel/lib/index.css";
import loading_img from "./../asset/loading_iframe.gif";
import { LEGEND_SECTIONS, getStatusStyle } from "./../statusStyles";
import { PlusIcon, MinusIcon, ExternalLinkIcon } from "./../icons";
import "./../style.css";

// Shown while a block is being dragged to another space, so the drag reads as
// distinct from every status colour.
const STATUS_DRAGGING_BG = "#992e2f";

// Keep in step with the 768px breakpoint in style.css.
const NARROW_VIEWPORT = 768;
const SIDEBAR_WIDTH_MAX = 400;
const SIDEBAR_WIDTH_DEFAULT = 140;
// Narrow enough to leave the grid usable on a phone, wide enough to still show
// a room name. A fixed 140px was 39% of a 360px viewport.
const SIDEBAR_WIDTH_MIN_NARROW = 84;
// Below this the zoom row drops its "Zoom" label so the two buttons fit. With
// the 40px touch-sized buttons the full row needs ~130px.
const ZOOM_LABEL_MIN_SIDEBAR_WIDTH = 140;

/*
 * Card density.
 *
 * Short cards are one line (the customer or block name), so many rows fit on
 * screen. Detailed cards show three: the name, the booked plan (or the block's
 * kind), and the time. Rows grow to fit them. The choice is remembered per
 * browser; storage can be blocked inside an iframe, which just means the
 * matrix opens on short cards again.
 */
const CARD_DENSITY_STORAGE_KEY = "matrix.cardDensity";
const ROW_HEIGHT = { short: 30, detailed: 72 };

const readCardDensity = () => {
  try {
    return window.localStorage.getItem(CARD_DENSITY_STORAGE_KEY) === "detailed"
      ? "detailed"
      : "short";
  } catch {
    return "short";
  }
};

const storeCardDensity = (density) => {
  try {
    window.localStorage.setItem(CARD_DENSITY_STORAGE_KEY, density);
  } catch {
    // Not persisted; the toggle still works for this visit.
  }
};

moment.locale("en");

// The CORE app's language codes (URL `l`, settings messages) to moment's.
// `zh` is Traditional Chinese, as in i18n.js.
const MOMENT_LOCALES = {
  en: "en",
  fr: "fr",
  ja: "ja",
  zh: "zh-tw",
  zh_cn: "zh-cn",
};

const momentLocaleFor = (lang) => MOMENT_LOCALES[lang] ?? "en";

/*
 * Date patterns per moment locale. Names (month, weekday) come from the
 * locale; the order does not, so Chinese and Japanese get year-first patterns
 * (2026年10月27日) instead of the English order with translated names. Times
 * stay 24-hour (HH:mm) everywhere.
 */
const WESTERN_DATES = {
  dayMonth: "D MMM", // a booking's start and end on different days
  date: "ddd D MMM YYYY", // tooltips
  monthYear: "MMMM YYYY", // primary header, month view
  longDate: "ddd D MMMM YYYY", // primary header, day view
  longDateNoWeekday: "D MMMM YYYY",
  dayOfMonth: "dd D", // bottom header, one column per day
};
const CJK_DATES = {
  dayMonth: "M月D日",
  date: "YYYY年M月D日 (ddd)",
  monthYear: "YYYY年M月",
  longDate: "YYYY年M月D日 dddd",
  longDateNoWeekday: "YYYY年M月D日",
  dayOfMonth: "D (dd)",
};
const DATE_FORMATS = {
  en: WESTERN_DATES,
  fr: WESTERN_DATES,
  ja: CJK_DATES,
  "zh-tw": CJK_DATES,
  "zh-cn": CJK_DATES,
};

// The patterns for the language a moment was made in (moments keep the
// locale that was global when they were created).
const formatsFor = (m) => DATE_FORMATS[m.locale()] ?? WESTERN_DATES;

// The card's time line. Expenses sit on whole days at midnight, where a clock
// time would be noise, so those show nothing.
const cardTimeLabel = (start, end) => {
  if (!start || !end) return null;
  const isDateOnly =
    start.hour() === 0 &&
    start.minute() === 0 &&
    end.hour() === 0 &&
    end.minute() === 0;
  if (isDateOnly) return null;
  return start.isSame(end, "day")
    ? `${start.format("HH:mm")} – ${end.format("HH:mm")}`
    : `${start.format(`${formatsFor(start).dayMonth} HH:mm`)} – ${end.format(`${formatsFor(end).dayMonth} HH:mm`)}`;
};

/*
 * Lazy loading.
 *
 * The timeline draws three screen-widths of canvas (the library's default
 * `buffer`), but the matrix used to fetch only the one on screen, and only once
 * a drag or wheel had stopped — so every day dragged into view started empty.
 * It now fetches a screen-width either side of the view, and fetches again as
 * soon as the view comes within half that margin of the edge of what has been
 * requested, while the drag is still going.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
// Caps the margin at the widest zooms, where a screen is up to 60 days.
const PREFETCH_MAX_MARGIN = 30 * DAY_MS;
// A fling crosses the prefetch edge many times a second; one request per
// quarter-second is plenty.
const FETCH_THROTTLE_MS = 250;

const getPrefetchMargin = (visibleStart, visibleEnd) =>
  Math.min(visibleEnd - visibleStart, PREFETCH_MAX_MARGIN);

// The range to fetch for a view: a margin either side, in whole days, since
// the API takes dates.
const fetchRangeAround = (visibleStart, visibleEnd) => {
  const margin = getPrefetchMargin(visibleStart, visibleEnd);
  return {
    start: moment(visibleStart - margin)
      .startOf("day")
      .valueOf(),
    end: moment(visibleEnd + margin)
      .endOf("day")
      .valueOf(),
  };
};

const rangeCovers = (range, start, end) =>
  Boolean(range) && range.start <= start && range.end >= end;

const rangeOverlaps = (range, start, end) =>
  Boolean(range) && range.start <= end && range.end >= start;

const getSidebarWidthFloor = () =>
  typeof window !== "undefined" && window.innerWidth <= NARROW_VIEWPORT
    ? SIDEBAR_WIDTH_MIN_NARROW
    : SIDEBAR_WIDTH_DEFAULT;

// Which state flag holds the "struck through in the legend" bit per status.
const STRIKE_STATE_KEYS = {
  pending: "isPendingStrike",
  confirmed: "isConfirmStrike",
  precheckin: "isPrecheckinStrike",
  checkin: "isCheckinStrike",
  checkout: "isCheckoutStrike",
  renewed: "isRenewStrike",
  timeblock: "isTimeblockStrike",
  expenses: "isExpensesStrike",
  cancelled: "isCancelStrike",
};

/*
 * Hover text for a block, rendered by the browser as a native tooltip.
 *
 * Deliberately native rather than a styled popover: the blocks already carry
 * fragile pointer handling (interact.js drag, plus the cross-engine
 * double-tap detection in handleItemTap), and hanging mouseenter/mouseleave
 * state off every block risks disturbing it for a cosmetic gain. The trade is
 * that the browser decides the delay and the styling.
 */
const buildTooltip = ({ title, detail, start, end, resourceName, origin }) => {
  const DATE = formatsFor(start).date;
  // Expenses are stored as whole days, so both ends sit on midnight; printing
  // "00:00 – 00:00" for them would be noise rather than information.
  const isDateOnly =
    start.hour() === 0 &&
    start.minute() === 0 &&
    end.hour() === 0 &&
    end.minute() === 0;

  let range;
  if (isDateOnly) {
    range = start.isSame(end, "day")
      ? start.format(DATE)
      : `${start.format(DATE)} – ${end.format(DATE)}`;
  } else if (start.isSame(end, "day")) {
    range = `${start.format(DATE)} · ${start.format("HH:mm")} – ${end.format("HH:mm")}`;
  } else {
    range = `${start.format(`${DATE} HH:mm`)} – ${end.format(`${DATE} HH:mm`)}`;
  }

  return [title, detail, range, resourceName, origin]
    .filter(Boolean)
    .join("\n");
};

/*
 * Header labels.
 *
 * The library's defaults repeat the full date in every cell of the bottom row
 * once you zoom in — an hour column reads "09/23/2026, 08:00", so the month and
 * year are restated dozens of times across a single screen. The bottom row now
 * carries only the position within the day (or the day within the month), and
 * the date context lives once, in the primary row above it.
 */
const bottomHeaderLabel = ([startTime], unit, labelWidth) => {
  switch (unit) {
    case "year":
      return startTime.format("YYYY");
    case "month":
      return startTime.format(labelWidth < 60 ? "MMM" : "MMMM");
    case "day":
      return startTime.format(
        labelWidth < 40 ? "D" : formatsFor(startTime).dayOfMonth,
      );
    case "hour":
      return startTime.format(labelWidth < 40 ? "HH" : "HH:mm");
    default:
      return startTime.format("HH:mm");
  }
};

/*
 * Bottom-row cell. The same markup the library renders by default, plus an
 * `is-weekend` class on Saturday and Sunday so the header can mark them.
 *
 * Weekends are marked here and not in the grid below. A tinted grid column
 * read as closed or unavailable — the same grey as the space-group bands —
 * which is wrong for hotels and anything else that trades on weekends. The
 * header keeps the orientation without saying anything about capacity.
 *
 * Only for day- and hour-wide cells: a month or year cell starting on a
 * weekend is not "a weekend".
 */
const bottomHeaderInterval = ({ getIntervalProps, intervalContext }) => {
  const { startTime, endTime } = intervalContext.interval;
  const day = startTime.day();
  const isWeekend =
    (day === 0 || day === 6) && endTime.diff(startTime, "days", true) <= 1;
  return (
    <div
      {...getIntervalProps()}
      className={isWeekend ? "rct-dateHeader is-weekend" : "rct-dateHeader"}
    >
      <span>{intervalContext.intervalText}</span>
    </div>
  );
};

/*
 * The primary row's unit is one step coarser than the timeline's, so it is the
 * month when showing days and the day when showing hours. Either way it always
 * spells out the year, where the default drops to "MMMM" and then "MM/YY" as
 * the column narrows — now that this is the only row carrying the date, it can
 * never be the row that omits it.
 */
const primaryHeaderLabel = ([startTime], unit) => {
  switch (unit) {
    case "year":
      return startTime.format("YYYY");
    case "month":
      return startTime.format(formatsFor(startTime).monthYear);
    case "day":
      return startTime.format(formatsFor(startTime).longDate);
    default:
      return startTime.format(formatsFor(startTime).longDateNoWeekday);
  }
};

const getInitialSidebarWidth = () => {
  if (typeof window === "undefined") return SIDEBAR_WIDTH_DEFAULT;
  if (window.innerWidth > NARROW_VIEWPORT) return SIDEBAR_WIDTH_DEFAULT;
  return Math.max(
    SIDEBAR_WIDTH_MIN_NARROW,
    Math.round(window.innerWidth * 0.28),
  );
};

// Two taps on the same item within this many ms are a double-click.
const DOUBLE_TAP_MS = 500;
// Calls closer than this are one tap reported twice (see handleItemTap).
const SAME_TAP_MS = 60;

class SpaceOrderCalendar extends Component {
  constructor(props) {
    super(props);
    this.lastTapItemId = null; // 上一次点击的item id，用于跨引擎检测双击
    // When the last tap landed, kept here rather than in state: setState is
    // asynchronous, and two calls for one click can read a stale value.
    this.lastTapAt = 0;
    // Where the timeline is looking, in ms. Kept off state: it changes on every
    // scroll frame and nothing renders from it.
    this.visibleRange = {
      start: moment().startOf("day").add(-10, "day").valueOf(),
      end: moment().startOf("day").add(10, "day").valueOf(),
    };
    this.requestedRange = null; // range of the latest request, answered or not
    this.fetchSeq = 0; // numbers each request
    this.appliedSeq = 0; // the request whose response is on screen
    this.pendingFetches = 0;
    this.lastFetchAt = 0;
    this.fetchTimer = null;
    this.state = {
      timeout: "", // 记录定时器
      openPanel: false, // 控制右侧面板的显示和隐藏
      pandelHeader: "Order Info", // 面板标题
      loading: false, // 加载状态
      token: "", // token
      loadedRange: null, // range of the response on screen, in ms
      isFetching: false, // a request is in flight
      fetchError: false, // the latest request failed
      openGroups: {}, // 控制space的展开和收缩
      resources: [], // 资源订单
      expenses: [], // 费用订单
      newGroups: [], // 新的space
      newItems: [], // 新的order
      // What i18n starts in (i18n.js: lng "en"): applySettings only switches when
      // the language differs, so a different value here made `?l=zh` a no-op.
      currentLanguage: "en",
      theme: "light", // 当前主题
      timezone: "Asia/Shanghai", // 当前时区
      // The node this matrix is for (sent by the CMS). A booking on a shared
      // resource whose nodeId differs belongs to another node.
      node: null,
      timezoneOffset: 0, // 当前时区偏移
      statusFilter: [
        "unassigned",
        "pending",
        "confirmed",
        "precheckin",
        "checkin",
        "checkout",
        "renewed",
        "timeblock",
        "expenses",
      ], // order状态过滤
      isCancelStrike: true,
      isPendingStrike: false,
      isConfirmStrike: false,
      isPrecheckinStrike: false,
      isCheckinStrike: false,
      isCheckoutStrike: false,
      isRenewStrike: false,
      isTimeblockStrike: false,
      isExpensesStrike: false,
      isItemDragging: false,
      sidebarWidth: getInitialSidebarWidth(), // Initial sidebar width
      isResizing: false, // Sidebar open state
      cardDensity: readCardDensity(), // "short" or "detailed" cards
    };
  }

  /**
   * Applies display settings from the CORE app: on load from the URL (l, t, d)
   * and later from its messages, so switching language, theme or card density
   * there updates the matrix in place instead of reloading it. Unknown or
   * missing values are left alone.
   */
  applySettings = ({ lang, theme, density }) => {
    const languageChanged =
      typeof lang === "string" && lang && lang !== this.state.currentLanguage;
    if (languageChanged) {
      // Before i18n: its change re-renders, and the dates built then must be
      // made in the new locale.
      moment.locale(momentLocaleFor(lang));
      this.props.i18n.changeLanguage(lang);
    }
    if (theme === "light" || theme === "dark") {
      document.getElementsByTagName("body")[0].className = theme;
    }
    if (density === "detailed" || density === "short")
      storeCardDensity(density);
    this.setState({
      ...(languageChanged ? { currentLanguage: lang } : {}),
      ...(theme === "light" || theme === "dark" ? { theme } : {}),
      ...(density === "detailed" || density === "short"
        ? { cardDensity: density }
        : {}),
    });
  };

  // Block texts are translated when they are built, so a language change
  // rebuilds them -- once the new `t` has arrived, which is after
  // changeLanguage, on the re-render it triggers.
  componentDidUpdate = (prevProps) => {
    if (prevProps.t !== this.props.t && this.state.loadedRange) {
      this.buildTimeline(this.state.loadedRange);
    }
  };

  // Messages from the CORE app hosting this matrix: from the parent page on
  // the web, or posted to this window by the app's web view.
  handleHostMessage = (event) => {
    if (event.source !== window.parent && event.source !== window) return;
    let data = event.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    if (data?.type === "matrixSettings") this.applySettings(data);
  };

  componentDidMount = () => {
    // 在组件挂载时绑定全局鼠标释放事件
    // document.addEventListener("mousedown", this.handleMouseDown);
    // document.addEventListener("mouseup", this.handleMouseUp);
    // 触摸屏事件
    document.addEventListener("touchend", this.handleMouseUp);
    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);
    window.addEventListener("resize", this.handleViewportResize);
    window.addEventListener("message", this.handleHostMessage);
    // Tells the CORE app this matrix takes settings messages, and lets it send
    // the current ones (a switch flipped while the matrix was loading).
    const ready = { type: "matrixReady", settings: true };
    window.parent?.postMessage(ready, "*");
    window.MATRIX_CHANNEL?.postMessage(JSON.stringify(ready));

    document.addEventListener("mousedown", (e) => {
      let target = e.target;
      while (target !== document) {
        if (target.classList && target.classList.contains("rct-sidebar-row")) {
          console.log("Resizing should start");
          this.handleMouseDownResize(e); // Adjust to start resizing only if correct conditions are met
          break;
        }
        target = target.parentNode;
      }
    });

    // PointerEvent兼容性判断
    if (window.PointerEvent) {
      // PointerEvent事件
      document.addEventListener("pointerup", this.handleMouseUp);
    } else if (window.MSPointerEvent) {
      // IE10的PointerEvent事件
      document.addEventListener("MSPointerUp", this.handleMouseUp);
    }
    // 获取外部传入的参数
    // get url from url params
    const location = window.location.href;
    // 包含token和body值
    const params = location.substring(location.lastIndexOf("/") + 1);
    // 获取token
    var token = params.split("?")[0];
    // 获取语言和theme设置
    var search_params = params.split("?")[1];

    // The first fetch covers the initial view plus its prefetch margin, widened
    // to any startTime/endTime the URL asks for.
    const initialRange = fetchRangeAround(
      this.visibleRange.start,
      this.visibleRange.end,
    );
    if (search_params !== undefined) {
      // 获取参数
      const keys = search_params.split("&").reduce((res, item) => {
        const [key, val] = item.split("=");
        res[key] = val;
        return res;
      }, {});
      // 设置开始时间和结束时间
      if (keys.startTime !== undefined) {
        initialRange.start = Math.min(
          initialRange.start,
          moment(keys.startTime).startOf("day").valueOf(),
        );
      }
      if (keys.endTime !== undefined) {
        initialRange.end = Math.max(
          initialRange.end,
          moment(keys.endTime).endOf("day").valueOf(),
        );
      }
      // Language (l), theme (t) and card density (d) chosen in the CORE app.
      this.applySettings({ lang: keys.l, theme: keys.t, density: keys.d });
    }
    // 设置state参数
    this.setState({ token: token }, () => {
      this.querySpaceOrder(initialRange);
    });
  };

  componentWillUnmount = () => {
    // 在组件卸载时解绑全局鼠标释放事件
    // document.removeEventListener("mousedown", this.handleMouseDown);
    // document.removeEventListener("mouseup", this.handleMouseUp);
    // 触摸屏事件
    document.removeEventListener("touchend", this.handleMouseUp);
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);
    document.removeEventListener("mousedown", this.handleMouseUp);
    window.removeEventListener("message", this.handleHostMessage);
    window.removeEventListener("resize", this.handleViewportResize);
    clearTimeout(this.fetchTimer);
    // PointerEvent兼容性判断
    if (window.PointerEvent) {
      // PointerEvent事件
      document.removeEventListener("pointerup", this.handleMouseUp);
    } else if (window.MSPointerEvent) {
      // IE10的PointerEvent事件
      document.removeEventListener("MSPointerUp", this.handleMouseUp);
      return false;
    }
  };

  handleMouseDownResize = () => {
    this.setState({ isResizing: true });
  };

  handleMouseMove = (event) => {
    if (this.state.isResizing) {
      const floor = getSidebarWidthFloor();
      if (event.clientX <= SIDEBAR_WIDTH_MAX && event.clientX >= floor) {
        this.setState({ sidebarWidth: event.clientX });
      }
    }
  };

  // Rotating a phone changes which sidebar floor applies, so a sidebar left
  // narrow in portrait would stay narrow in landscape.
  handleViewportResize = () => {
    this.setState((prevState) => {
      const floor = getSidebarWidthFloor();
      if (prevState.sidebarWidth >= floor) return null;
      return { sidebarWidth: floor };
    });
  };

  // Every way of moving the view — drag, trackpad, zoom buttons, pinch, a
  // click on a week in the header — reaches here, so this is the one place
  // that decides whether to load more.
  handleTimeChange = (visibleTimeStart, visibleTimeEnd, updateScrollCanvas) => {
    updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
    this.visibleRange = { start: visibleTimeStart, end: visibleTimeEnd };
    this.ensureLoaded();
  };

  // Fetch once the view comes within half a margin of the edge of what has
  // been requested. Compared against the request rather than the response, so
  // a slow request is not sent again on every frame while it is in flight.
  ensureLoaded = () => {
    const { start, end } = this.visibleRange;
    const slack = getPrefetchMargin(start, end) / 2;
    if (rangeCovers(this.requestedRange, start - slack, end + slack)) return;

    clearTimeout(this.fetchTimer);
    const fetchVisible = () =>
      this.querySpaceOrder(
        fetchRangeAround(this.visibleRange.start, this.visibleRange.end),
      );
    const wait = FETCH_THROTTLE_MS - (Date.now() - this.lastFetchAt);
    if (wait <= 0) {
      fetchVisible();
    } else {
      this.fetchTimer = setTimeout(fetchVisible, wait);
    }
  };

  retryFetch = () => {
    this.querySpaceOrder(
      fetchRangeAround(this.visibleRange.start, this.visibleRange.end),
    );
  };

  handleMouseUp = (event) => {
    if (this.state.isResizing) {
      this.setState({ isResizing: false });
      console.log("Resizing has ended");
    }
  };

  /**
   * 根据category排序，在返回的Groups中，General Expenses 和 Unassigned 必须排在最前面
   * 1. 找到 General Expenses 和 Unassigned 的 root 对象
   * 2. 获取子对象
   * 3. 获取 General Expenses 及其子对象
   * 4. 获取 Unassigned 及其子对象
   * 5. 剩余的对象（不属于 General Expenses 和 Unassigned）
   * 6. 组合最终排序后的数组
   * 7. 返回排序后的数组(newGroups)
   * @param {*} arr
   * @returns
   */
  groupsSortByCategory = (arr) => {
    // 找到 General Expenses 和 Unassigned 的 root 对象
    const generalExpenses = arr.find((item) => item.tag === "General Expenses");
    const unassigned = arr.find((item) => item.title === "Unassigned");
    // 获取子对象
    const getChildren = (parentId) =>
      arr.filter((item) => item.parent === parentId);
    // 获取 General Expenses 及其子对象
    const generalExpensesGroup = generalExpenses
      ? [generalExpenses, ...getChildren(generalExpenses.id)]
      : [];
    // 获取 Unassigned 及其子对象
    const unassignedGroup = unassigned
      ? [unassigned, ...getChildren(unassigned.id)]
      : [];
    // 剩余的对象（不属于 General Expenses 和 Unassigned）
    const remaining = arr.filter(
      (item) =>
        !generalExpensesGroup.includes(item) && !unassignedGroup.includes(item),
    );
    // 组合最终排序后的数组
    return [...unassignedGroup, ...remaining, ...generalExpensesGroup];
  };

  getExpenseTimespans = (
    _items,
    timespans,
    differenceInHours,
    _groups,
    t,
    claimId,
  ) => {
    // Expenses share one colour regardless of category. The old
    // getExpenseCategoryColors() enumerated eleven categories that all mapped
    // to the identical blue, then derived opacity by string-replacing "rgb"
    // with "rgba" — which turned its own "rgba(...)" fallback into the invalid
    // "rgbaa(...)", so an unrecognised category rendered unstyled.
    const expenseStyle = getStatusStyle("expenses");
    const status2 = "expenses";

    timespans.forEach((span, index) => {
      const expenseStart = moment(moment(span.startTime).format("YYYY-MM-DD"));
      const expenseEnd = moment(moment(span.endTime).format("YYYY-MM-DD"));
      const actualTitle = `${get(span, "name")} ${get(span, "currency")}${get(span, "actualAmount")}`;
      const budgetTitle = `${get(span, "name")} ${get(span, "currency")}${get(span, "budgetAmount")}`;
      let _currentGroupId = _groups
        ? _groups.find(
            (group) => group.title === span.resources[0].name && !group.root,
          )
        : undefined;
      if (span.actualAmount !== "0") {
        _items.push({
          // A recurring expense repeats its id on every occurrence.
          id: claimId(`expense:${span.id}:${span.startTime}`),
          group: _currentGroupId?.id,
          title: actualTitle,
          div_title: buildTooltip({
            title: actualTitle,
            detail: t(span.category) || span.category,
            start: expenseStart,
            end: expenseEnd,
            resourceName: get(span, "resources[0].name"),
          }),
          start_time: expenseStart,
          end_time: expenseEnd,
          status: status2,
          color: expenseStyle.text,
          bgColor: expenseStyle.tint,
          selectedBgColor: expenseStyle.solid,
          selectedColor: expenseStyle.textOnSolid,
          stripeColor: expenseStyle.stripe,
          selectedBorder: "2px",
          selected: false,
          className:
            moment(span.startTime).day() === 6 ||
            moment(span.endTime).day() === 0
              ? "item-weekend"
              : "",
          itemProps: {
            style: {
              display: "block",
            },
            mode: "expenses",
            type: "expenses",
            category: span.category,
            targetId: span.id,
          },
        });
      }
      if (span.actualAmount === "0" && span.budgetAmount !== "0") {
        _items.push({
          // A recurring expense repeats its id on every occurrence.
          id: claimId(`expense:${span.id}:${span.startTime}`),
          group: _currentGroupId?.id,
          title: budgetTitle,
          div_title: buildTooltip({
            title: budgetTitle,
            detail: t(span.category) || span.category,
            start: expenseStart,
            end: expenseEnd,
            resourceName: get(span, "resources[0].name"),
          }),
          start_time: expenseStart,
          end_time: expenseEnd,
          status: status2,
          color: expenseStyle.text,
          bgColor: expenseStyle.tint,
          selectedBgColor: expenseStyle.solid,
          selectedColor: expenseStyle.textOnSolid,
          stripeColor: expenseStyle.stripe,
          // Budgeted but not yet spent: dimmed rather than given its own
          // colour, so the distinction survives in both themes. A bespoke pale
          // tint would have vanished in dark, where blocks use a solid fill.
          budget: true,
          selectedBorder: "2px",
          selected: false,
          className:
            moment(span.startTime).day() === 6 ||
            moment(span.endTime).day() === 0
              ? "item-weekend"
              : "",
          itemProps: {
            style: {
              display: "block",
            },
            mode: "expenses",
            category: span.category,
            targetId: span.id,
          },
        });
      }
    });
  };

  /**
   * 加载SpaceOrder
   *
   * Loads `range` (ms) and redraws the matrix from it. Called without one, it
   * reloads the latest requested range, as after a block is moved.
   */
  async querySpaceOrder(range = this.requestedRange) {
    if (!range) return;
    clearTimeout(this.fetchTimer);
    const seq = ++this.fetchSeq;
    this.requestedRange = range;
    this.lastFetchAt = Date.now();
    this.pendingFetches += 1;
    this.setState({ isFetching: true, fetchError: false });

    const options = {
      method: "POST",
      url: `${process.env.REACT_APP_PAYLOAD_URL}/core/api/v1/matrix/timespans`,
      // url: `${process.env.REACT_APP_PAYLOAD_URL}/core/api/v1/matrix/expenses`,
      // url: 'https://venom.exp.is/core/api/v1/matrix/timespans',
      headers: {
        "content-type": "application/json",
        Authorization: this.state.token,
      },
      data: JSON.stringify({
        startTime: moment(range.start).format("YYYY-MM-DD"),
        endTime: moment(range.end).format("YYYY-MM-DD"),
        // "startTime": '2023-01-01',
        // "endTime"  : '2023-01-31'
      }),
    };

    let response = null;
    try {
      response = await axios.request(options);
    } catch (error) {
      console.error("[matrix] failed to load timespans", error);
    } finally {
      this.pendingFetches -= 1;
      if (this.pendingFetches === 0) this.setState({ isFetching: false });
    }

    // Responses can land out of order. Drop any older than the one on screen,
    // but not merely older than the latest request: during a long drag that
    // would discard every response until the drag stopped.
    if (seq < this.appliedSeq) return;

    // A successful reply always carries the "Unassigned" tag. On a server-side
    // error the API still answers 200, with empty tags — treat that as a
    // failure too, rather than wiping the matrix.
    if (!response?.data?.tags?.length) {
      if (seq === this.fetchSeq) {
        // Forget the range, so the next move of the view asks for it again.
        this.requestedRange = this.state.loadedRange;
        this.setState({ fetchError: true });
      }
      return;
    }
    this.appliedSeq = seq;

    const resources = response.data.tags; // Response received from the API
    const expenses = response.data.expenses ?? [];
    const timezone = response.data.timezone;
    const node = response.data.node ?? null;
    // 获取浏览器所在的时区, 以便计算时区差异（分钟）
    const localTimezone = moment.tz.guess();
    // const localTimezoneOffset = new Date().getTimezoneOffset();
    const localTimezoneOffset = moment.tz(localTimezone).utcOffset();
    // console.log(localTimezoneOffset)
    // 获取 Asia/Tokyo 的偏移（分钟）
    const tokyoTimezoneOffset = moment.tz(timezone).utcOffset();
    // console.log(tokyoTimezoneOffset)
    // 计算时区差异（以小时为单位）
    const differenceInHours = (tokyoTimezoneOffset - localTimezoneOffset) / 60;
    // console.log('差距时间H', differenceInHours)

    // 将resources追加到state.resources中
    this.setState(
      {
        timezone: timezone,
        timezoneOffset: differenceInHours,
        node,
        resources: resources,
        expenses: expenses,
        // resources: _.unionBy(resources, this.state.resources, 'id')
      },
      () => this.buildTimeline(range),
    );
  }

  // Builds the timeline's groups and blocks from the loaded data. Separate from
  // the fetch so a language change can rebuild the blocks' texts (status,
  // tooltips) without loading the data again.
  buildTimeline = (range) => {
    const differenceInHours = this.state.timezoneOffset;
    // The matrix's own node, to mark bookings from another one.
    const node = this.state.node;
    const { t } = this.props;
    // IDs come from the data rather than being generated per fetch, so a
    // refetch updates the blocks React already has instead of remounting
    // every one of them — and a selected block stays selected.
    const usedIds = new Set();
    const claimId = (base) => {
      let id = base;
      for (let n = 2; usedIds.has(id); n++) id = `${base}~${n}`;
      usedIds.add(id);
      return id;
    };
    // 空间树分组
    var _groups = [];
    // 空间树订单
    var _items = [];
    // 费用订单, 由于此时_groups为空，可以不用判断_groups中是否存在类型，直接进行添加
    this.state.expenses.forEach(({ name, resources }, indexs) => {
      // 遍历expenses的空间及空间下的订单资源
      console.log("indexs", indexs);
      if (name === "General Expenses") {
        _groups.push({
          id: indexs + 1,
          title: t("generalExpenses"),
          root: true,
          tag: name,
        });
        // 遍历expenses的空间下的订单所属的资源
        resources.forEach((item, index) => {
          // Expense categories come without an id.
          var groupId = claimId(`expense-group:${item.name}`);
          _groups.push({
            id: groupId,
            title: item.name,
            parent: indexs + 1,
            rightTitle: item.name,
            itemProps: {
              targetId: item.id,
              mode: "expenses",
            },
          });
          this.getExpenseTimespans(
            _items,
            item.timespans,
            differenceInHours,
            undefined,
            t,
            claimId,
          );
        });
      }
    });

    // 现在开始添加订单的信息，由于_groups已经存在了相应的对象，因此每一个添加都必须判断
    this.state.resources.forEach(({ name, resources }, indexs) => {
      // 查看_groups中是否已经存在同名的对象
      const group = _groups.find((group) => group.title === name);
      if (!group) {
        _groups.push({
          id: _groups.filter((group) => group.root).length + 1,
          title: name,
          root: true,
          tag: name,
        });
      }
      resources.forEach((item, index) => {
        const expense = this.state.expenses.find(
          (expense) => expense.name === item.name,
        );
        const expenseTimespan = expense?.resources
          ? expense.resources[0].timespans
          : [];
        // const group = _groups.find(group => group.title === item.name);
        // if (!group) {
        // "Unassigned" is a resource without an id.
        var groupId = claimId(`resource:${item.id ?? item.name}`);
        _groups.push({
          id: groupId,
          title: item.name,
          parent: _groups.find((group) => group.title === name).id,
          rightTitle: item.name,
          itemProps: {
            targetId: item.id,
          },
        });
        // }

        this.getExpenseTimespans(
          _items,
          expenseTimespan,
          differenceInHours,
          _groups,
          t,
          claimId,
        );

        item.timespans.forEach((span, index) => {
          // This chain decides the status only; the colours for it come
          // from statusStyles.js, which the legend reads too.
          var status2 = "others";
          if (span.purpose === "Order") {
            if (span.topping) {
              const toppingStatus = span.topping.status?.toLowerCase();

              if (
                span.metadata?.disableExtraTime &&
                toppingStatus !== "cancelled"
              ) {
                status2 = "timeblock";
              } else if (toppingStatus === "pending") {
                status2 = "pending";
              } else if (toppingStatus === "confirmed") {
                status2 = "confirmed";
              } else if (toppingStatus === "precheckedin") {
                status2 = "precheckin";
              } else if (toppingStatus === "checkedin") {
                status2 = "checkin";
              } else if (toppingStatus === "checkedout") {
                status2 = "checkout";
              } else if (toppingStatus === "renewed") {
                status2 = "renewed";
              } else {
                status2 = "cancelled";
              }
            } else {
              status2 = "timeblock";
            }
          } else if (span.purpose === "unassigned") {
            if (span.topping?.status === "cancelled") {
              status2 = "cancelled";
            } else {
              status2 = "unassigned";
            }
          } else if (
            ["Cleaning", "Maintenance", "VIP", "others"].includes(span.purpose)
          ) {
            status2 = "timeblock";
          }
          const statusStyle = getStatusStyle(status2);
          let _currentGroupId = _groups.find(
            (group) => group.title === item.name && !group.root,
          );
          const itemStart = moment(span.startTime).add(
            differenceInHours,
            "hours",
          );
          const itemEnd = moment(span.endTime).add(differenceInHours, "hours");
          const isBooking =
            span.purpose === "Order" || span.purpose === "unassigned";
          // Only known when the CMS sends the matrix's node; otherwise no
          // booking is marked, as before.
          const fromOtherNode =
            isBooking &&
            Boolean(node && span.nodeId) &&
            span.nodeId.toLowerCase() !== node.toLowerCase();
          const itemTitle =
            get(span, "purpose") === "Order" ||
            get(span, "purpose") === "unassigned"
              ? `${[get(span, "topping.order.customers[0].lastName"), get(span, "topping.order.customers[0].firstName")].filter(Boolean).join(" ")} #${span.orderId}`
              : get(span, "name");
          _items.push({
            id: claimId(`timespan:${span.id}`),
            group: _currentGroupId.id,
            title: itemTitle,
            // Second line of a detailed card: the booked plan, or the
            // status where there is no plan (a time block, or a response
            // from a CMS that does not send the plan name yet).
            subtitle: get(span, "topping.name") || t(status2),
            // Native browser tooltip — the timeline passes this through as
            // the block's `title` attribute via keys.itemDivTitleKey.
            div_title: buildTooltip({
              title: itemTitle,
              detail: t(status2),
              start: itemStart,
              end: itemEnd,
              resourceName: item.name,
              origin: fromOtherNode
                ? t("fromOtherNode", { node: span.nodeId })
                : undefined,
            }),
            start_time: itemStart,
            end_time: itemEnd,
            fromOtherNode,
            status: status2,
            color: statusStyle.text,
            bgColor: statusStyle.tint,
            selectedBgColor: statusStyle.solid,
            selectedColor: statusStyle.textOnSolid,
            stripeColor: statusStyle.stripe,
            selectedBorder: "2px",
            selected: false,
            className:
              moment(span.startTime).day() === 6 ||
              moment(span.endTime).day() === 0
                ? "item-weekend"
                : "",
            itemProps: {
              style: {
                display: "block",
              },
              type: "order",
              purpose: span.purpose,
              ...(span.purpose === "Order" || span.purpose === "unassigned"
                ? {
                    "data-tip": "tooltip",
                    orderId: span.orderId,
                    timespanId: span.id,
                    toppingId: span.toppingId,
                    nodeId: span.nodeId,
                    planName: get(span, "topping.name"),
                  }
                : {}),
              ...(["Cleaning", "Maintenance", "VIP", "others"].includes(
                span.purpose,
              )
                ? {
                    "data-tip": "tooltip",
                    targetId: span.id,
                  }
                : {}),
            },
          });
        });
      });
    });
    // _groups时没有排序之前的Group对象数组
    // console.log('原始的groups', _groups)
    // console.log('原始的items', _items)

    // 将General Expenses及其子对象放在第一位，Unassigned及其对象为第二位，其余自然
    _groups = this.groupsSortByCategory(_groups);
    // console.log('排序后的groups', _groups);

    _items = _items.sort(function (a, b) {
      return b - a;
    });
    // 遍历items2，将group设置为对应的group的id
    console.log("排序后的items", _items);
    this.setState((prevState) => ({
      newGroups: _groups,
      newItems: _items,
      loading: true,
      loadedRange: range,
      fetchError: false,
      // On the first load every space group starts open.
      ...(prevState.loading
        ? {}
        : {
            openGroups: _groups
              .filter((group) => group.root)
              .reduce((open, group) => ({ ...open, [group.id]: true }), {}),
          }),
    }));
  };

  // Build the detail payload for an item and post it to the parent CORE app
  // (via postMessage) so it can open the timespan/order details panel.
  emitItemDetail = (item) => {
    let obj = {};
    if (
      item.itemProps.purpose === "Order" ||
      item.itemProps.purpose === "unassigned"
    ) {
      obj = {
        orderId: item.itemProps.orderId,
        toppingId: item.itemProps.toppingId,
        type: "matrixOrderMatrix",
        // What the CORE app shows before opening an order from another node
        // (a booking on a shared resource), and the node to switch to.
        nodeId: item.itemProps.nodeId,
        planName: item.itemProps.planName,
        startTime: moment(item.start_time).format("YYYY-MM-DD HH:mm"),
        endTime: moment(item.end_time).format("YYYY-MM-DD HH:mm"),
      };
    } else if (
      ["Cleaning", "Maintenance", "VIP", "others"].includes(
        item.itemProps.purpose,
      )
    ) {
      obj = {
        targetId: item.itemProps.targetId,
        type: "matrixTimespan",
      };
    } else if (item.itemProps.mode === "expenses") {
      obj = {
        targetId: item.itemProps.targetId,
        type: "expenseTimespan",
      };
    }
    console.log("[matrix] emitItemDetail -> posting to parent", obj);
    window.parent?.postMessage(obj, "*");
    // The app's JavaScript channel takes exactly one string. On Android it is
    // a Java bridge that matches methods by argument count, so a second
    // argument ("*", as window.postMessage takes) made it throw "Method not
    // found" and the double tap never reached the app. iOS ignored the extra.
    window.MATRIX_CHANNEL?.postMessage(JSON.stringify(obj));
  };

  // Double-click/tap detection driven by the timeline's own click pipeline
  // (onItemSelect / onItemClick), NOT by DOM event handlers.
  //
  // Why: react-calendar-timeline mounts interact.js on an item once it is
  // selected, and interact.js preventDefaults the pointer events. On Blink
  // (Chrome) and Gecko (Firefox) that suppresses the compatibility
  // mouse/touch events, so native dblclick, mouseup and touchend never reach
  // React — which is why double-click only worked on WebKit (Safari/iOS).
  // interact.js's own `tap` gesture still fires on every engine and routes
  // through the library's actualClick -> onItemSelect / onItemClick, so we do
  // the double detection there. Two taps on the same item within 500ms open
  // the details.
  handleItemTap = (itemId) => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - this.lastTapAt;
    clearTimeout(this.state.timeout);

    // One physical click can reach here twice: Firefox fires both
    // onItemSelect and onItemClick for it, a few milliseconds apart. Counted
    // as two taps, every click looked like a double-click and a double-click
    // opened the details twice. Calls this close together are the same tap;
    // even a fast human double-click is slower.
    if (this.lastTapItemId === itemId && tapLength < SAME_TAP_MS) {
      return;
    }

    if (this.lastTapItemId === itemId && tapLength < DOUBLE_TAP_MS) {
      console.log(
        `[matrix] double-click detected on item ${itemId} (gap ${tapLength}ms)`,
      );
      const item = this.state.newItems.find((i) => i.id === itemId);
      if (item) {
        this.emitItemDetail(item);
      } else {
        console.warn(`[matrix] no item found in newItems for id ${itemId}`);
      }
      // Reset so a third tap doesn't immediately re-trigger.
      this.lastTapItemId = null;
      this.lastTapAt = 0;
      return;
    }
    console.log(
      `[matrix] single tap on item ${itemId} — waiting for a 2nd tap within ${DOUBLE_TAP_MS}ms`,
    );
    const timer = setTimeout(function () {
      clearTimeout(timer);
    }, DOUBLE_TAP_MS);
    this.lastTapItemId = itemId;
    this.lastTapAt = currentTime;
    this.setState({ timeout: timer });
  };

  itemRenderer = ({
    item,
    timelineContext,
    itemContext,
    getItemProps,
    getResizeProps,
  }) => {
    const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();
    // A booking from another node (shared resource) leads there when opened:
    // the External Link icon says so before the title.
    const title = item.fromOtherNode ? (
      <>
        <span
          style={{
            display: "inline-block",
            width: "var(--sizing-icon-xs)",
            height: "var(--sizing-icon-xs)",
            marginRight: "var(--spacing-4)",
            verticalAlign: "-1px",
          }}
        >
          <ExternalLinkIcon />
        </span>
        {itemContext.title}
      </>
    ) : (
      itemContext.title
    );

    // A block is a pale tint with dark text and a saturated left stripe — the
    // design system's tag.light shape. Selecting one flips it to the saturated
    // fill (tag.solid). Colours come from statusStyles.js via the item.
    //
    // Dark theme uses the saturated fill throughout: the pale tints are mixed
    // towards white, so on a black canvas they glare and stop reading as a
    // tint at all. This is the design system's own tag.light/tag.solid split —
    // light surfaces take the tint, dark surfaces take the solid.
    const isDark = this.state.theme === "dark";
    const selected = itemContext.selected;
    const useSolid = isDark || selected;
    const backgroundColor =
      selected && itemContext.dragging
        ? STATUS_DRAGGING_BG
        : useSolid
          ? item.selectedBgColor
          : item.bgColor;
    const textColor = useSolid ? item.selectedColor : item.color;
    // On a solid fill the stripe would be the same colour as the block, so it
    // is dropped there and selection is shown with an outline instead — in
    // dark theme the fill alone cannot signal it.
    const stripe = isDark ? "none" : `3px solid ${item.stripeColor}`;
    const outline =
      isDark && selected ? "2px solid var(--color-border-strong)" : undefined;

    return (
      <div
        {...getItemProps({
          itemTouchSendsClick: true,
          style: {
            backgroundColor,
            fontSize: "var(--typography-font-size-text-xs)",
            padding: 0,
            color: textColor,
            display: item.itemProps.style.display,
            border: "none",
            // The stripe is a left border rather than a child element so it
            // cannot be clipped by the block's own overflow handling.
            borderLeft: stripe,
            outline,
            outlineOffset: "-2px",
            borderRadius: "var(--radius-small)",
            // A budgeted-but-unspent cost reads as provisional in both themes.
            opacity: item.budget ? 0.55 : 1,
            overflow: "hidden",
          },
          onMouseDown: () => {},
          onMouseUp: () => {},
        })}
      >
        {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : null}
        {this.state.cardDensity === "detailed" ? (
          <div
            style={{
              height: itemContext.dimensions.height,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              overflow: "hidden",
              padding: "0 var(--spacing-6)",
              lineHeight: 1.35,
            }}
          >
            {[
              [title, "var(--typography-font-weight-semi-bold)"],
              [item.subtitle, "var(--typography-font-weight-regular)"],
              [
                cardTimeLabel(item.start_time, item.end_time),
                "var(--typography-font-weight-regular)",
              ],
            ]
              .filter(([text]) => text)
              .map(([text, fontWeight], line) => (
                <div
                  key={line}
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {text}
                </div>
              ))}
          </div>
        ) : (
          <div
            style={{
              height: itemContext.dimensions.height,
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              padding: "0 var(--spacing-6)",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: "var(--typography-font-weight-medium)",
            }}
          >
            {title}
          </div>
        )}
        {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : null}
      </div>
    );
  };

  toggleGroup = (id) => {
    const { openGroups } = this.state;
    this.setState(
      {
        openGroups: {
          ...openGroups,
          [id]: !openGroups[id],
        },
      },
      () => {
        // console.log(this.state.openGroups)
      },
    );
  };

  // Was nine near-identical methods, one per status.
  toggleStrike = (status) => {
    const key = STRIKE_STATE_KEYS[status];
    if (!key) return;
    this.setState((prevState) => ({ [key]: !prevState[key] }));
  };

  handleItemMove = (itemId, dragTime, newGroupOrder) => {
    const { newItems, newGroups } = this.state;
    // 被移动到的space
    const group = newGroups[newGroupOrder];
    // order不能被放置到root为true的space树根节点中，只能放到具体的space中
    if (!group.root) {
      const currentItem = newItems.filter((item) =>
        item.id === itemId ? item : null,
      );
      this.setState(
        {
          newItems: newItems.map((item) =>
            item.id === itemId
              ? Object.assign({}, item, {
                  group: group.id,
                })
              : item,
          ),
        },
        async () => {
          const selectedItem = this.state.newItems.filter((item) =>
            item.id === itemId ? item : null,
          );
          const selectedSpace = this.state.newGroups.filter((group) =>
            group.id === selectedItem[0].group ? group : null,
          );
          // console.log('被选中的节点', currentItem[0])
          // console.log('被选中的space', selectedSpace[0])
          // 如果是在同一房间内拖动，不做处理
          if (currentItem[0].group === selectedSpace[0].id) {
            return;
          }
          const result = await confirm(`Confirm change?`, {});
          if (result) {
            // item不选中
            console.log("item不选中");
            this.setState({
              isItemDragging: false,
            });
            const options = {
              method: "PATCH",
              url: `${process.env.REACT_APP_PAYLOAD_URL}/core/api/v1/matrix/timespans`,
              headers: {
                "content-type": "application/json",
                Authorization: this.state.token,
              },
              data: JSON.stringify({
                orderId: selectedItem[0].itemProps.orderId,
                toppingId: selectedItem[0].itemProps.toppingId,
                targetId: selectedSpace[0].itemProps.targetId,
                timespanId: selectedItem[0].itemProps.timespanId,
              }),
            };
            // console.log(options)
            await axios.request(options);
            this.querySpaceOrder();
            return;
          } else {
            this.setState({
              newItems: newItems.map((item) =>
                item.id === itemId
                  ? Object.assign({}, item, {
                      group: currentItem[0].group,
                    })
                  : item,
              ),
            });
          }
        },
      );
    }
  };

  // handleItemResize = (itemId, time, edge) => {
  //     const { newItems } = this.state;

  //     this.setState({
  //         newItems: newItems.map(item =>
  //             item.id === itemId
  //             ? Object.assign({}, item, {
  //                 start: edge === "left" ? time : item.start,
  //                 end: edge === "left" ? item.end : time
  //                 })
  //             : item
  //         )
  //     });
  //     console.log("Resized", itemId, time, edge);
  // };

  /**
   * 根据statusItem过滤newItems
   * @param {*} statusItem
   */
  filterOrder = (statusItem) => {
    this.setState((prevState) => {
      const statusFilter = prevState.statusFilter.includes(statusItem)
        ? prevState.statusFilter.filter((item) => item !== statusItem)
        : [...prevState.statusFilter, statusItem];
      return { statusFilter };
    });
    // console.log('statusItem', statusItem)
    // newsItems中设置itemProps.type为statusItem的item, 且status为statusItem的item的style.display为none
    // 再次点击时，恢复
    // 如果stateItem为timeblock
    if (statusItem === "timeblock") {
      console.log("设置timeblock", statusItem);
      this.setState({
        newItems: this.state.newItems.map((item) => {
          if (
            item.status === "timeblock" &&
            item.itemProps.style.display === "block"
          ) {
            item.itemProps.style.display = "none";
          } else if (
            item.status === "timeblock" &&
            item.itemProps.style.display === "none"
          ) {
            item.itemProps.style.display = "block";
          }
          return item;
        }),
      });
    } else if (statusItem === "expenses") {
      this.setState({
        newItems: this.state.newItems.map((item) => {
          if (
            item.status === "expenses" &&
            item.itemProps.style.display === "block"
          ) {
            item.itemProps.style.display = "none";
          } else if (
            item.status === "expenses" &&
            item.itemProps.style.display === "none"
          ) {
            item.itemProps.style.display = "block";
          } else if (
            item.itemProps.type === "expenses" &&
            item.status === statusItem &&
            item.itemProps.style.display === "block"
          ) {
            item.itemProps.style.display = "none";
          } else if (
            item.itemProps.type === "expenses" &&
            item.status === statusItem &&
            item.itemProps.style.display === "none"
          ) {
            item.itemProps.style.display = "block";
          }
          return item;
        }),
      });
    }
  };

  /**
   * 将objects中的item的group设置为groups中对应的group的id
   * @param {*} objects
   * @param {*} groups
   * @returns
   */
  assignItem2Group = (objects, groups) => {
    return objects.map((item) => {
      if (item.group === undefined) {
        // 在 groups 数组中找到 title 匹配 category 的对象
        // console.log(item.itemProps.category)
        // console.log(groups)
        const matchedGroup = groups.find(
          (group) => group.rightTitle === item.itemProps.category,
        );
        // console.log(matchedGroup)
        if (matchedGroup) {
          // console.log(matchedGroup.id)
          return { ...item, group: matchedGroup.id }; // 赋值 group
        } else {
          // console.log(groups)
          // const matchedGroup2 = groups.find(group => group.tag === 'Unassigned');
          // console.log('matchGroup2', matchedGroup2)
          // return {...item, group: matchedGroup2.id}; // 赋值 group
        }
      }
      return item;
    });
  };

  /*
   * Marks the grid columns outside the loaded range, so a day that has not
   * arrived yet reads as loading rather than as empty.
   *
   * Only columns entirely outside it. Day and hour columns fall wholly inside
   * or outside the range, whose ends are whole days, but at the widest zooms —
   * the default on a phone — a column is a month, and the current one always
   * runs past the prefetch margin. Asking for full coverage greyed it out as
   * loading for good.
   *
   * The timeline redraws its
   * columns only when this function's identity changes, so it is rebuilt only
   * when the range or the error state does.
   */
  getColumnClassNames = (loadedRange, fetchError) => {
    if (
      this.columnClassNames &&
      this.columnClassNamesRange === loadedRange &&
      this.columnClassNamesError === fetchError
    ) {
      return this.columnClassNames;
    }
    const unloaded = fetchError
      ? ["is-unloaded", "is-load-failed"]
      : ["is-unloaded"];
    this.columnClassNamesRange = loadedRange;
    this.columnClassNamesError = fetchError;
    this.columnClassNames = (start, end) =>
      rangeOverlaps(loadedRange, start, end) ? [] : unloaded;
    return this.columnClassNames;
  };

  render() {
    const { t } = this.props;
    const {
      loading,
      newGroups,
      newItems,
      openGroups,
      statusFilter,
      timezoneOffset,
      loadedRange,
      isFetching,
      fetchError,
    } = this.state;
    console.log(statusFilter);
    const filterNewItems = newItems.filter((item) =>
      statusFilter.includes(item.status),
    );
    // console.log('filterNewItems', filterNewItems)
    // 获取当前时间的timestamp
    // 获取 Asia/Tokyo 的当前时间的timestamp
    const tokyoCurrentTimestamp =
      moment().valueOf() + timezoneOffset * 60 * 60 * 1000;
    // console.log(tokyoCurrentTimestamp)

    const newGroups2 = newGroups
      .filter((g) => g.root || openGroups[parseInt(g.parent)])
      .map((group) => {
        return Object.assign({}, group, {
          title: group.root ? (
            <div
              className="group-label"
              onClick={() => this.toggleGroup(parseInt(group.id))}
            >
              <span className="group-label-toggle">
                {openGroups[parseInt(group.id)] ? "−" : "+"}
              </span>
              <span>{t(group.title)}</span>
            </div>
          ) : (
            <div className="group-child-label">{t(group.title)}</div>
          ),
        });
      });

    // let newFilterNewItems = [];
    // if(newGroups2.length > 3){
    //     console.log('newGroups2', newGroups2[3].id)
    //     newFilterNewItems = filterNewItems.map(item => {
    //         if(item.group === newGroups2[3].id){
    //             item.group = 2;
    //         }
    //         return item;
    //     });
    // }
    // console.log('newFilterNewItems', newFilterNewItems)
    // 如果filterNewItem的元素的group是xGFpW字符串，则统一改为2
    // filterNewItems.forEach(item => {
    //     if(item.group === newGroups2[3].id){
    //         item.group = 2;
    //     }
    // });

    // 遍历filterNewItems，如果它的itemProps存在mode属性，且mode为timeblock，则查询它的group与newGroups2的id是否相等，
    // 如果相等，则将它的group设置为newGroups2的parent
    // filterNewItems.forEach(item => {
    //     if(item.itemProps.mode && item.itemProps.mode === 'timeblock'){
    //         const group = newGroups2.find(group => group.id === item.group);
    //         if(group){
    //             item.group = group.parent;
    //         }
    //     }
    // });
    // console.log(newItems)

    const newItems2 = this.assignItem2Group(filterNewItems, newGroups2);

    const fetchStatus = (
      <>
        {isFetching && <div className="fetch-progress" aria-hidden="true" />}
        {fetchError && !isFetching && (
          <div className="fetch-error" role="alert">
            <span>{t("loadFailed")}</span>
            <button type="button" onClick={this.retryFetch}>
              {t("retry")}
            </button>
          </div>
        )}
      </>
    );

    return loading ? (
      <div onClick={() => {}}>
        <>
          {fetchStatus}
          <Timeline
            ref={(r) => {
              this.timeline = r;
            }}
            horizontalLineClassNamesForGroup={(group) => {
              return group.root ? ["root"] : "";
            }}
            groups={newGroups2}
            items={newItems2}
            keys={{
              groupIdKey: "id",
              groupTitleKey: "title",
              groupRightTitleKey: "rightTitle",
              itemIdKey: "id",
              itemTitleKey: "title",
              itemDivTitleKey: "div_title",
              itemGroupKey: "group",
              itemTimeStartKey: "start_time",
              itemTimeEndKey: "end_time",
              groupLabelKey: "title",
            }}
            sidebarWidth={this.state.sidebarWidth}
            itemsSorted
            itemTouchSendsClick={false}
            stackItems={true}
            itemHeightRatio={0.75}
            showCursorLine
            canMove={true}
            canResize={false}
            lineHeight={ROW_HEIGHT[this.state.cardDensity]}
            minZoom={3 * 60 * 60 * 1000}
            maxZoom={1 * 60 * 86400 * 1000}
            timeSteps={{
              minute: 15,
              hour: 1,
              day: 1,
              month: 1,
              year: 1,
            }}
            defaultTimeStart={moment(this.visibleRange.start)}
            defaultTimeEnd={moment(this.visibleRange.end)}
            verticalLineClassNamesForTime={this.getColumnClassNames(
              loadedRange,
              fetchError,
            )}
            itemRenderer={this.itemRenderer}
            onBoundsChange={(canvasTimeStart, canvasTimeEnd) => {
              // console.log('canvasTimeStart', moment(canvasTimeStart).add(25, "days").format("YYYY-MM-DD"))
              // console.log('canvasTimeEnd', moment(canvasTimeEnd).add(-25, "days").format("YYYY-MM-DD"))
              // this.setState({
              //     resources : [],
              //     startTime : moment(canvasTimeStart).format("YYYY-MM-DD"),
              //     endTime   : moment(canvasTimeEnd).format("YYYY-MM-DD")
              // },()=>{
              //     console.log('拖曳时间',  moment(canvasTimeStart).format("YYYY-MM-DD"), moment(canvasTimeEnd).format("YYYY-MM-DD"))
              //     this.querySpaceOrder();
              // });
              // console.log('拖曳时间',  moment(canvasTimeStart).format("YYYY-MM-DD"), moment(canvasTimeEnd).format("YYYY-MM-DD"))
              // this.querySpaceOrder();
            }}
            onInteractionEnd={() =>
              console.log("Mouse released or interaction ended")
            }
            onTimeChange={this.handleTimeChange}
            onItemSelect={(itemId, e, time) => {
              console.log("Item选中");
              this.setState({
                isItemDragging: true,
              });
              // First tap of a potential double-click (item was unselected).
              this.handleItemTap(itemId);
            }}
            onItemClick={(itemId, e, time) => {
              // Tap on an already-selected item — the second tap of a
              // double-click. Fires on all engines via interact.js's tap.
              this.handleItemTap(itemId);
            }}
            onItemMove={this.handleItemMove}
            onTouchEnd={(itemId, e, time) => {
              console.log("触摸屏结束");
            }}
            // onItemResize={this.handleItemResize}
            // handleTimeChange = {(visibleTimeStart, visibleTimeEnd,updateScrollCanvas) => {
            //     updateScrollCanvas(moment(this.state.defaultTimeStart).valueOf(),
            //     moment(this.state.defaultTimeEnd).valueOf());
            // }}
          >
            <TimelineHeaders>
              <SidebarHeader>
                {({ getRootProps, showPeriod }) => {
                  // The root props pin this cell to the sidebar's width. On the
                  // title alone they left the cell sized by the zoom row, which
                  // outgrows a narrow sidebar and shifts the time columns right
                  // of the grid lines.
                  const compact =
                    this.state.sidebarWidth < ZOOM_LABEL_MIN_SIDEBAR_WIDTH;
                  return (
                    <div className="sidebar-header" {...getRootProps()}>
                      {/* was inline `color: white`, i.e. invisible against the
                          light theme's own white header */}
                      <div className="sidebar-header-title">
                        <span className="sidebar-header-label">
                          {t("space")}
                        </span>
                      </div>

                      <div
                        className={`zoom-controls${compact ? " is-compact" : ""}`}
                      >
                        <button
                          type="button"
                          aria-label={`${t("zoom")} -`}
                          onClick={() => this.timeline.changeZoom(1.5)}
                        >
                          <MinusIcon />
                        </button>
                        <span>{t("zoom")}</span>
                        <button
                          type="button"
                          aria-label={`${t("zoom")} +`}
                          onClick={() => this.timeline.changeZoom(0.5)}
                        >
                          <PlusIcon />
                        </button>
                      </div>
                    </div>
                  );
                }}
              </SidebarHeader>
              <DateHeader
                unit="primaryHeader"
                style={{ backgroundColor: "var(--color-surface)" }}
                labelFormat={primaryHeaderLabel}
              />
              <CustomHeader
                height={22}
                headerData={{ someData: "data" }}
                unit="week"
              >
                {({
                  headerContext: { intervals },
                  getRootProps,
                  getIntervalProps,
                  showPeriod,
                  data,
                }) => {
                  return (
                    <div {...getRootProps()}>
                      {intervals.map((interval) => {
                        const intervalStyle = {
                          textAlign: "center",
                          borderLeft: "1px solid var(--color-border-subtle)",
                          cursor: "pointer",
                          backgroundColor: "var(--color-surface)",
                          color: "var(--color-text-secondary)",
                        };
                        // Zoomed far enough out that a full "Week 42" per
                        // column would not fit.
                        const isYearView = intervals.length > 100;
                        return (
                          <div
                            key={interval.startTime.valueOf()}
                            onClick={() => {
                              showPeriod(interval.startTime, interval.endTime);
                            }}
                            {...getIntervalProps({
                              interval,
                              style: intervalStyle,
                            })}
                          >
                            <span className="rct-dateHeader">
                              {t(
                                isYearView ? "weekNumberShort" : "weekNumber",
                                {
                                  week: interval.startTime.format("W"),
                                },
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              </CustomHeader>
              <DateHeader
                labelFormat={bottomHeaderLabel}
                intervalRenderer={bottomHeaderInterval}
              />
            </TimelineHeaders>
            <CustomMarker date={tokyoCurrentTimestamp}>
              {({ styles, date }) => {
                const customStyles = {
                  ...styles,
                  backgroundColor: "var(--color-feedback-error-bg-strong)",
                  width: "2px",
                };
                return <div style={customStyles} onClick={() => {}} />;
              }}
            </CustomMarker>
            {/* <TodayMarker date={tokyoCurrentTimestamp} >
                            {({ styles, date }) => {
                                const customStyles = {
                                    ...styles,
                                    backgroundColor: 'red',
                                    width: '3px'
                                }
                                return <div style={customStyles} onClick={()=>{}} />
                            }}
                        </TodayMarker> */}
          </Timeline>
          <section id="legend" aria-labelledby="legend-title">
            <h2 id="legend-title" className="legend-title">
              {t("legend")}
            </h2>
            {LEGEND_SECTIONS.map(({ key, statuses }) => {
              const shown = statuses.filter(
                (status) =>
                  status !== "expenses" || this.state.expenses.length > 0,
              );
              if (shown.length === 0) return null;
              return (
                <div className="legend-section" key={key}>
                  <h3
                    id={`legend-section-${key}`}
                    className="legend-section-title"
                  >
                    {t(key)}
                  </h3>
                  <ul
                    className="legend-list"
                    aria-labelledby={`legend-section-${key}`}
                  >
                    {shown.map((status) => {
                      const isVisible = statusFilter.includes(status);
                      return (
                        <li key={status}>
                          {/* The swatch and the label used to be separate
                              cells with different handlers — the swatch
                              filtered without striking — so the two could
                              disagree about the same status. One control now
                              does both. */}
                          <button
                            type="button"
                            className={
                              this.state[STRIKE_STATE_KEYS[status]]
                                ? "legend-item is-struck"
                                : "legend-item"
                            }
                            aria-pressed={isVisible}
                            onClick={() => {
                              this.filterOrder(status);
                              this.toggleStrike(status);
                            }}
                          >
                            <span
                              className="legend-swatch"
                              style={{
                                backgroundColor: getStatusStyle(status).stripe,
                              }}
                            />
                            <span className="legend-label">{t(status)}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </section>
        </>
      </div>
    ) : (
      <>
        {/* Outside #loading: its transform would pin the fixed-position
            status bar to the spinner rather than to the viewport. */}
        {fetchStatus}
        {!fetchError && (
          <div id="loading" className="loading">
            <img
              src={loading_img}
              style={{ width: "48px", height: "48px" }}
              alt=""
            />
          </div>
        )}
      </>
    );
  }
}

export default withTranslation()(SpaceOrderCalendar);
